/**
 * Camada EMPRESA do painel de detalhes da conversa.
 *
 * É aqui que mora o documento (CNPJ/CPF), o dado que mais se pede no
 * atendimento. Só o miolo — a moldura (voltar, título, rolagem) é do
 * `DetailsLayer`.
 *
 * Um detalhe que decide o desenho: o nome do cliente já vem resolvido na
 * própria conversa (`companyName`), enquanto o documento depende da rota de
 * dropdown de clientes, que pode ainda não ter respondido — ou não trazer
 * aquele cliente. Nesse meio-tempo a camada CONTINUA existindo, com o nome e um
 * travessão no documento: devolver `null` aqui deixaria a pessoa numa
 * sub-página em branco, depois de ela ter clicado numa linha do índice que
 * prometia a empresa.
 */
import React from 'react';
import { PrivacyText } from '@/shared/components/ui/PrivacyText';

import type { DetailsData } from '../useDetailsData';

interface CompanyLayerProps {
  data: DetailsData;
}

const TRACO = '—';

/**
 * O aviso não tem classe própria no `.cdp-*`: é uma frase discreta abaixo das
 * linhas, e as peças existentes (`.cdp-empty`, `.cdp-k`) têm outro papel.
 */
const ESTILO_AVISO: React.CSSProperties = {
  color: 'var(--theme-muted-text)',
  fontSize: 12,
  lineHeight: 1.45,
};

/** Vazio, só espaço em branco e `undefined` são a mesma coisa: "não temos". */
function limpar(valor: string | undefined): string | undefined {
  const texto = valor?.trim();
  return texto ? texto : undefined;
}

const CompanyLayer: React.FC<CompanyLayerProps> = ({ data }) => {
  const { company, companyName } = data;

  // O `limpar` antes do `??` é o que faz um nome gravado em branco no cadastro
  // do cliente cair para o nome que veio na conversa. Com `??` sobre o valor
  // cru, string vazia venceria e a camada sumiria por causa de um campo vazio.
  const nome = limpar(company?.name) ?? limpar(companyName);

  if (!nome) {
    return (
      <div data-testid="details-company">
        <p className="cdp-empty" data-testid="details-company-empty">
          Este contato não está vinculado a nenhuma empresa.
        </p>
      </div>
    );
  }

  const documento = limpar(company?.document);

  // O rótulo do documento é o próprio tipo do cadastro. Sem o cadastro em mãos
  // não dá para afirmar CNPJ nem CPF — aí a linha se chama "Documento".
  const rotuloDocumento =
    company?.type === 'PJ' ? 'CNPJ' : company?.type === 'PF' ? 'CPF' : 'Documento';
  const tipo =
    company?.type === 'PJ' ? 'Pessoa jurídica' : company?.type === 'PF' ? 'Pessoa física' : TRACO;

  return (
    <div data-testid="details-company">
      <div className="cdp-kv">
        <span className="cdp-k">Cliente vinculado</span>
        <span className="cdp-v" data-testid="details-company-name">
          {nome}
        </span>
      </div>

      <div className="cdp-kv">
        <span className="cdp-k">{rotuloDocumento}</span>
        {/* Documento é dado sensível como telefone e e-mail: entra no modo
            privacidade junto com o resto. O travessão não precisa de borrão. */}
        {/* O testid fica no `span` de fora porque `PrivacyText` só repassa
            children/as/className/style/title — não espalha o resto das props. */}
        <span className="cdp-v" data-testid="details-company-document">
          {documento === undefined ? TRACO : <PrivacyText>{documento}</PrivacyText>}
        </span>
      </div>

      <div className="cdp-kv">
        <span className="cdp-k">Tipo</span>
        <span className="cdp-v" data-testid="details-company-type">
          {tipo}
        </span>
      </div>

      {/* Sem o cadastro do cliente em mãos não dá para saber se o documento
          está a caminho ou se não existe. A frase diz o que é verdade nos dois
          casos — afirmar "não foi possível carregar" seria dar como falha o que
          na maioria das vezes é só a busca ainda em curso. */}
      {!company && (
        <p className="mt-2" style={ESTILO_AVISO} data-testid="details-company-partial">
          Documento não disponível no momento.
        </p>
      )}
    </div>
  );
};

export default CompanyLayer;
